const SOCIAL_PLATFORM_HOSTS = new Set([
  'facebook.com',
  'm.facebook.com',
  'fb.com',
  'instagram.com',
  'linkedin.com',
  'twitter.com',
  'x.com',
  'youtube.com',
  'tiktok.com',
  'threads.net',
]);

const SOCIAL_RESERVED_PATHS = new Set([
  'pages',
  'people',
  'profile.php',
  'watch',
  'reel',
  'reels',
  'stories',
  'p',
  'explore',
  'accounts',
  'login',
  'share',
  'groups',
  'events',
  'photos',
  'videos',
  'posts',
  'about',
  'home',
  'feed',
  'in',
  'company',
]);

const GENERIC_RESEARCH_REPORT_NAME_PATTERN = /^(Company|Person)\s+(Research|Background)\s+Report$/i;

const socialTitleCache = new Map<string, string>();

export type SocialProfile = {
  platform: string;
  host: string;
  slug: string;
  label: string;
  url: string;
};

export type ResearchTargetNameInput = {
  targetType?: 'company' | 'person';
  personName?: string;
  companyName?: string;
  website?: string;
};

export type ResearchSubjectTitleInput = {
  reportName?: string;
  targetName?: string;
  website?: string;
  targetType?: string;
};

function firstCsvValue(raw: string): string {
  return String(raw || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)[0] || '';
}

function hostnameFromWebsite(raw: string): string {
  const website = String(raw || '').trim();
  if (!website) return '';
  try {
    const parsed = new URL(website.includes('://') ? website : `https://${website}`);
    return parsed.hostname.replace(/^www\./i, '').trim() || website;
  } catch {
    return website.split('/')[0].trim();
  }
}

export function isSocialPlatformHost(value: string): boolean {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return false;
  const host = (text.includes('.') || text.includes('://'))
    ? hostnameFromWebsite(text).replace(/^www\./i, '')
    : text;
  if (SOCIAL_PLATFORM_HOSTS.has(host)) return true;
  return Array.from(SOCIAL_PLATFORM_HOSTS).some((item) => item.split('.')[0] === host);
}

export function humanizeSocialSlug(slug: string): string {
  let text = String(slug || '').trim();
  try {
    text = decodeURIComponent(text);
  } catch {
    // Keep the raw slug when it is not percent-encoded.
  }
  text = text.replace(/[_+]+/g, ' ').replace(/-/g, ' ').trim();
  text = text.replace(/([a-z])([A-Z])/g, '$1 $2');
  text = text.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  text = text.replace(/([a-zA-Z])([A-Z]{2})$/g, '$1 $2');
  text = text.replace(/\b([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñ]{2,})(de|del)\b/g, '$1 $2');
  return text.replace(/\s+/g, ' ').trim();
}

export function socialProfileFromUrl(value: string): SocialProfile | null {
  const text = String(value || '').trim();
  if (!text) return null;
  const candidate = text.includes('://')
    ? text
    : (/\.(com|net)\//i.test(text) ? `https://${text}` : '');
  if (!candidate) return null;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }
  let host = (parsed.hostname || '').trim().toLowerCase();
  if (host.startsWith('www.')) host = host.slice(4);
  if (!SOCIAL_PLATFORM_HOSTS.has(host)) return null;
  const parts = parsed.pathname.split('/').map((part) => part.trim()).filter(Boolean);
  let slug = parts[0] || '';
  if (host === 'linkedin.com' && parts.length) {
    slug = (parts[0] === 'in' || parts[0] === 'company') && parts[1] ? parts[1] : '';
  }
  if (!slug || SOCIAL_RESERVED_PATHS.has(slug.toLowerCase()) || slug.toLowerCase().startsWith('profile.php')) {
    return {
      platform: host.split('.')[0],
      host,
      slug: '',
      label: '',
      url: candidate,
    };
  }
  return {
    platform: host.split('.')[0],
    host,
    slug,
    label: humanizeSocialSlug(slug),
    url: candidate,
  };
}

export function companySubjectFromWebsite(raw: string): string {
  const social = socialProfileFromUrl(raw);
  if (social?.label) return social.label;
  const host = hostnameFromWebsite(raw);
  if (isSocialPlatformHost(host)) return '';
  return host;
}

export function deriveLocalResearchTargetName(input: ResearchTargetNameInput): string {
  const personName = firstCsvValue(String(input.personName || ''));
  const companyName = firstCsvValue(String(input.companyName || ''));
  const website = String(input.website || '').trim();
  const companySocial = socialProfileFromUrl(companyName);
  const usableCompany = companyName && !isSocialPlatformHost(companyName)
    ? (companySocial?.label || companyName)
    : (companySocial?.label || '');

  if (input.targetType === 'company') {
    if (usableCompany && !companySocial?.slug) return usableCompany;
    if (companySocial?.label) return companySocial.label;
    const fromWebsite = companySubjectFromWebsite(website);
    if (fromWebsite) return fromWebsite;
    return personName;
  }

  if (personName) return personName;
  if (usableCompany) return usableCompany;
  return companySubjectFromWebsite(website);
}

export function formatResearchSubjectTitle(report: ResearchSubjectTitleInput): string {
  const custom = String(report.reportName || '').trim();
  if (custom && !GENERIC_RESEARCH_REPORT_NAME_PATTERN.test(custom)) {
    return custom;
  }
  const targetName = String(report.targetName || '').trim();
  const website = String(report.website || '').trim();
  const fromWebsite = companySubjectFromWebsite(website);
  const host = hostnameFromWebsite(website);
  const hostIsSocial = isSocialPlatformHost(host) || isSocialPlatformHost(targetName);
  if (hostIsSocial || isSocialPlatformHost(targetName)) {
    if (fromWebsite && (!targetName || isSocialPlatformHost(targetName) || targetName.toLowerCase() === host.toLowerCase())) {
      return fromWebsite;
    }
    if (targetName && !isSocialPlatformHost(targetName)) {
      return targetName;
    }
    return fromWebsite || 'Report';
  }
  if (host && report.targetType === 'company' && (!targetName || targetName.toLowerCase() === host.toLowerCase())) {
    return host;
  }
  if (host && targetName && !targetName.toLowerCase().includes(host.toLowerCase()) && host.toLowerCase() !== targetName.toLowerCase()) {
    return `${targetName} · ${host}`;
  }
  return targetName || host || 'Report';
}

export async function fetchResolvedResearchTargetName(args: {
  gatewayBaseUrl: string;
  accessToken?: string;
  targetType: 'company' | 'person';
  targetName?: string;
  website?: string;
  timeoutMs?: number;
}): Promise<string> {
  const local = deriveLocalResearchTargetName({
    targetType: args.targetType,
    personName: args.targetType === 'person' ? args.targetName : '',
    companyName: args.targetType === 'company' ? args.targetName : '',
    website: args.website,
  });
  const social = socialProfileFromUrl(String(args.targetName || ''))
    || socialProfileFromUrl(String(args.website || ''));
  if (!social?.slug) return local;

  const cacheKey = String(social.url || '').trim().toLowerCase();
  const cached = socialTitleCache.get(cacheKey);
  if (cached) return cached;

  const gatewayBase = String(args.gatewayBaseUrl || '').replace(/\/$/, '');
  if (!gatewayBase) return local;

  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), Math.max(1500, args.timeoutMs || 5000));
    const response = await fetch(`${gatewayBase}/api/v1/research/resolve-target-name`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(args.accessToken ? { Authorization: `Bearer ${args.accessToken}` } : {}),
      },
      body: JSON.stringify({
        target_type: args.targetType,
        target_name: social.url || args.targetName || '',
        website: args.website || social.url,
      }),
      signal: controller.signal,
    });
    window.clearTimeout(timer);
    if (!response.ok) return local;
    const body = await response.json() as { target_name?: string };
    const resolved = String(body.target_name || '').trim();
    if (!resolved || isSocialPlatformHost(resolved)) return local;
    socialTitleCache.set(cacheKey, resolved);
    return resolved;
  } catch {
    return local;
  }
}
